import { Scene, GameObjects } from 'phaser';
import { audioManager } from '../core/CoreManagerAudio';
import { storage } from '../core/Storage';
import { getQuality, setQuality } from '../core/QualitySettings';
import { initLocalization, getLanguage, setLanguage, t } from '../i18n/Localization';

// Configuration Scene - Settings menu
export class Configuration extends Scene {
  private backButton!: GameObjects.Text;
  private title!: GameObjects.Text;
  private muteButton!: GameObjects.Text;
  private languageButton!: GameObjects.Text;
  private qualityButton!: GameObjects.Text;
  private resetButton!: GameObjects.Text;
  private isMuted: boolean = false;
  private returnScene: string = 'MainMenu';

  constructor() {
    super('Configuration');
  }

  init(data?: { returnScene?: string }): void {
    this.returnScene = data?.returnScene ?? 'MainMenu';
  }

  create(): void {
    initLocalization(this);
    const { width, height } = this.scale;

    // Background
    this.add.rectangle(width / 2, height / 2, width, height, 0x0d1f33, 1);

    // Animate scene in
    this.cameras.main.setAlpha(0);
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 1,
      duration: 300,
      ease: 'Power2'
    });

    // Title
    this.title = this.add.text(width / 2, height * 0.10, t('config.title'), {
      fontFamily: 'Crackvetica, Arial',
      fontSize: '48px',
      color: '#ff6b9d',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.title.setAlpha(0);
    this.tweens.add({
      targets: this.title,
      alpha: 1,
      y: { from: height * 0.05, to: height * 0.10 },
      duration: 400,
      ease: 'Back.easeOut'
    });

    // Options Panel Container - centered
    // Removed visual panel, keeping only text elements

    // Calculate Y positions for items
    const itemStartY = height * 0.30;
    const itemSpacing = 65;

    // Mute Button
    this.isMuted = audioManager.getMusicVolume() === 0 && audioManager.getSfxVolume() === 0;
    this.muteButton = this.add.text(width / 2, itemStartY, '', {
      fontFamily: 'Crackvetica, Arial',
      fontSize: '24px',
      color: '#9fffd0',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.updateMuteLabel();
    this.setupButtonInteractivity(this.muteButton, () => this.toggleMute());

    // Language Button
    this.languageButton = this.add.text(width / 2, itemStartY + itemSpacing, '', {
      fontFamily: 'Crackvetica, Arial',
      fontSize: '22px',
      color: '#ffd48a',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.updateLanguageLabel();
    this.setupButtonInteractivity(this.languageButton, () => this.toggleLanguage());

    // Quality Button
    this.qualityButton = this.add.text(width / 2, itemStartY + itemSpacing * 2, '', {
      fontFamily: 'Crackvetica, Arial',
      fontSize: '20px',
      color: '#a8f7ff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.updateQualityLabel();
    this.setupButtonInteractivity(this.qualityButton, () => this.toggleQuality());

    // Reset progress button - below panel
    this.resetButton = this.add.text(width / 2, height * 0.80, t('config.reset'), {
      fontFamily: 'Crackvetica, Arial',
      fontSize: '22px',
      color: '#ff9fa3',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.setupButtonInteractivity(this.resetButton, () => this.resetProgress());

    // Back button
    this.backButton = this.add.text(width / 2, height * 0.90, t('ui.back'), {
      fontFamily: 'Crackvetica, Arial',
      fontSize: '24px',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.setupButtonInteractivity(this.backButton, () => this.goBack());

    // Fade in all buttons
    [this.muteButton, this.languageButton, this.qualityButton, this.resetButton, this.backButton].forEach((btn, idx) => {
      btn.setAlpha(0);
      this.tweens.add({
        targets: btn,
        alpha: 1,
        delay: 150 + idx * 60,
        duration: 300,
        ease: 'Power2'
      });
    });
  }

  private setupButtonInteractivity(button: GameObjects.Text, callback: () => void): void {
    button.setInteractive({ useHandCursor: true });
    
    const originalScale = 1;
    const hoverScale = 1.1;
    const originalColor = button.style.color;

    button.on('pointerover', () => {
      this.tweens.add({
        targets: button,
        scaleX: hoverScale,
        scaleY: hoverScale,
        duration: 150,
        ease: 'Power2.easeOut'
      });
      button.setColor('#ffffff');
      audioManager.playUISound('hover', this);
    });

    button.on('pointerout', () => {
      this.tweens.add({
        targets: button,
        scaleX: originalScale,
        scaleY: originalScale,
        duration: 150,
        ease: 'Power2.easeOut'
      });
      button.setColor(originalColor as string);
    });

    button.on('pointerdown', () => {
      this.tweens.add({
        targets: button,
        scaleX: 0.95,
        scaleY: 0.95,
        duration: 100
      });
      audioManager.playUISound('click', this);
      callback();
    });

    button.on('pointerup', () => {
      this.tweens.add({
        targets: button,
        scaleX: hoverScale,
        scaleY: hoverScale,
        duration: 100
      });
    });
  }

  private toggleMute(): void {
    if (!this.isMuted) {
      audioManager.setMusicVolume(0);
      audioManager.setSfxVolume(0);
      this.isMuted = true;
    } else {
      audioManager.setMusicVolume(1);
      audioManager.setSfxVolume(1);
      this.isMuted = false;
    }
    this.updateMuteLabel();
  }

  private updateMuteLabel(): void {
    this.muteButton.setText(this.isMuted ? t('config.sound_off') : t('config.sound_on'));
  }

  private toggleLanguage(): void {
    const next = getLanguage() === 'es' ? 'en' : 'es';
    setLanguage(next);
    this.updateLanguageLabel();
    this.updateMuteLabel();
    this.updateQualityLabel();
    this.title.setText(t('config.title'));
    this.resetButton.setText(t('config.reset'));
    this.backButton.setText(t('ui.back'));
  }

  private updateLanguageLabel(): void {
    const code = t('menu.language_code');
    this.languageButton.setText(`${t('menu.language')}: ${code}`);
  }

  private toggleQuality(): void {
    const next = getQuality() === 'high' ? 'low' : 'high';
    setQuality(next);
    this.updateQualityLabel();
  }

  private updateQualityLabel(): void {
    const level = getQuality();
    const label = level === 'high' ? t('config.quality_high') : t('config.quality_low');
    this.qualityButton.setText(`${t('config.quality')}: ${label}`);
  }

  private async resetProgress(): Promise<void> {
    const confirmed = typeof window !== 'undefined' ? window.confirm(t('config.reset_confirm')) : true;
    if (!confirmed) return;

    await storage.resetAll();
    this.scene.start('MainMenu');
  }

  private goBack(): void {
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 0,
      duration: 200,
      ease: 'Power2',
      onComplete: () => {
        this.scene.start(this.returnScene);
      }
    });
  }
}
